import { isDateValid } from "@lyfegen-platform/utils";
import { addMonths, differenceInMonths, differenceInYears, endOfMonth, format, max, min, parse, startOfDay, startOfMonth } from "date-fns";
import { memo, useState } from "react";
import { Maybe } from "../../models/generated/graphql";
import DashboardDatePicker from "./DashboardDatePicker";
import CustomDatePicker from "./CustomDatePicker";
import { DateRangeLabelOptions, getRangeByOption } from "./utils";
import getDatePattern from "../../utils/date-pattern";

export interface IDatePickerProps {
	startDate?: Maybe<Date>;
	endDate?: Maybe<Date>;
	label?: string;
	required?: boolean;
	option?: Maybe<DateRangeLabelOptions>;
	contractStartDate?: Maybe<Date>;
	contractEndDate?: Maybe<Date>;
	enrollmentPeriods?: Maybe<Array<{ from: Date; to: Date }>>;
	selectMonths?: Maybe<boolean>;
	onChangeRange?: (startDate?: Maybe<Date>, endDate?: Maybe<Date>, option?: Maybe<DateRangeLabelOptions>) => void;
	labelStyles?: React.CSSProperties;
	isCaseEventLogFilter?: boolean;
	isAllTimeOptionAvailable?: boolean;
	error?: string;
	labelBg?: string;
	checkDate?: boolean;
	isDashboardDatePicker?: boolean;
}

export const dateFormatDateRange = getDatePattern();
export const dateFormatMonthRange = "MM-yyyy";
export const maskDates = getDatePattern(true);
export const maskMonths = "99-9999";
export const cohortLabel = "Cohort";

export const getOptionsToExclude = (contractStartDate: Maybe<Date>): Array<string> => {
	const monthDifference = differenceInMonths(new Date(), contractStartDate || 0);
	const yearDifference = differenceInYears(new Date(), contractStartDate || 0);
	const optionsToExclude = yearDifference < 1 ? ["lastCalYear"] : [];

	if (monthDifference < 1) {
		return [...optionsToExclude, "lastMonth", "last3months", "last6months", "last12months"];
	}
	if (monthDifference < 3) {
		return [...optionsToExclude, "last3months", "last6months", "last12months"];
	}
	if (monthDifference < 6) {
		return [...optionsToExclude, "last6months", "last12months"];
	}
	if (monthDifference < 12) {
		return [...optionsToExclude, "last12months"];
	}
	return optionsToExclude;
};

export function dateMaskFormat(selectMonths: boolean, months: string, dates: string) {
	return selectMonths ? months : dates;
}

function DatePicker(props: IDatePickerProps) {
	const dateFormat = dateMaskFormat(!!props.selectMonths, dateFormatMonthRange, dateFormatDateRange);
	const [state, setState] = useState<{
		anchorEl?: Maybe<Element>;
		startDate?: Maybe<Date>;
		endDate?: Maybe<Date>;
		option?: Maybe<DateRangeLabelOptions>;
		firstCalendarDate: Date;
		rawStartValue: string;
		rawEndValue: string;
	}>({
		anchorEl: undefined,
		startDate: props.startDate,
		endDate: props.endDate,
		option: props.option || null,
		firstCalendarDate: startOfMonth(props.endDate || props.startDate || new Date()),
		rawStartValue: props.startDate ? format(props.startDate, dateFormat) : "",
		rawEndValue: props.endDate ? format(props.endDate, dateFormat) : "",
	});
	const [startDateInvalid, setStartDateInvalid] = useState<boolean>();
	const [endDateInvalid, setEndDateInvalid] = useState<boolean>();
	const cohorts: Array<string> = [];
	const numberOfCohorts = props.enrollmentPeriods?.length || 0;

	const updateState = <T extends keyof typeof state>(key: T, value: typeof state[T]) => {
		setState((state) => ({
			...state,
			[key]: value,
		}));
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		updateState("anchorEl", event.currentTarget.parentElement);
	};

	const handleClose = () => {
		updateState("anchorEl", null);
	};

	const handleGoToNextMonth = () => {
		updateState("firstCalendarDate", addMonths(state.firstCalendarDate, 1));
	};

	const handleGoToPreviousMonths = () => {
		updateState("firstCalendarDate", addMonths(state.firstCalendarDate, -1));
	};

	const handleClickOption = (o: DateRangeLabelOptions) => {
		updateState("option", o);

		if (o !== "custom") {
			const [startDate, endDate] = getFilterStartAndEndDate(o.startsWith(cohortLabel), o);

			setStartDate(startDate);
			setEndDate(endDate);
			props.onChangeRange?.(startDate, endDate, o);
		}
	};

	const setStartDate = (value?: Maybe<Date>) => {
		updateState("startDate", value);
		updateState("rawStartValue", value ? format(value, dateFormat) : "");
	};

	const setEndDate = (value?: Maybe<Date>) => {
		updateState("endDate", value);
		updateState("rawEndValue", value ? format(value, dateFormat) : "");
	};

	const handleSelectDate = (date: Date) => {
		if ((!!state.startDate && !state.endDate) || (!state.startDate && !!state.endDate)) {
			const startDate = min([date, state.startDate || (state.endDate as Date)]);
			const endDate = max([date, state.startDate || (state.endDate as Date)]);
			setStartDate(startDate);
			setEndDate(endDate);
			props.onChangeRange?.(startDate, endDate, "custom");
			updateState("anchorEl", null);
		} else {
			setStartDate(date);
			setEndDate(null);
			props.onChangeRange?.(date, null, "custom");
		}
		updateState("option", "custom");
	};

	const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
		const rawStartValue = event.target.value === dateFormat.toLowerCase() ? "" : event.target.value;
		updateState("rawStartValue", rawStartValue);
		updateState("option", "custom");
		if (!rawStartValue.trim()) {
			updateState("startDate", null);
			props.onChangeRange?.(null, state.endDate, "custom");
			return;
		}

		const date = parse(rawStartValue, dateFormat, startOfDay(new Date()));
		setStartDateInvalid(!isDateValid(date));

		if (!endDateInvalid && isDateValid(date)) {
			updateState("startDate", date);
			if (!state.endDate || date <= state.endDate) {
				props.onChangeRange?.(date, state.endDate, "custom");
			}
		}
	};

	const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
		const rawEndValue = event.target.value === dateFormat.toLowerCase() ? "" : event.target.value;
		updateState("rawEndValue", rawEndValue);
		updateState("option", "custom");
		if (!rawEndValue.trim()) {
			updateState("endDate", null);
			props.onChangeRange?.(state.startDate, null, "custom");
			return;
		}

		const date = props.selectMonths
			? endOfMonth(parse(rawEndValue, dateFormat, new Date()))
			: parse(rawEndValue, dateFormat, new Date());

		setEndDateInvalid(!isDateValid(date));

		if (!startDateInvalid && isDateValid(date)) {
			updateState("endDate", date);
			if (!state.startDate || date >= state.startDate) {
				props.onChangeRange?.(state.startDate, date, "custom");
			}
		}
	};

	const handleLeaveFocus = (property: "rawStartValue" | "rawEndValue") => {
		const date = state[property];
		if (props.checkDate && !isDateValid(date)) {
			updateState(property, dateFormat);
		}
	};

	function getFilterStartAndEndDate(isCaob: boolean, o: DateRangeLabelOptions): [Maybe<Date>, Maybe<Date>] {
		if (isCaob) {
			const cohortNumber = Number(o.split(" ")[1]) - 1;
			const today = new Date();
			const to =
				props.enrollmentPeriods![cohortNumber].to.toISOString() > today.toISOString()
					? today
					: props.enrollmentPeriods![cohortNumber].to;
			return [props.enrollmentPeriods![cohortNumber].from, to];
		}
		return getRangeByOption(o, props.contractStartDate);
	}

	for (let i = 0; i < numberOfCohorts; i++) {
		cohorts.push(`${cohortLabel} ${i + 1}`);
	}
	const clearFilter = () => {
		setStartDate(null);
		setEndDate(null);
		updateState("option", null);
		props.onChangeRange?.(null, null, null);
	};

	return props.isDashboardDatePicker ? (
		<DashboardDatePicker
			{...props}
			state={state}
			startDateInvalid={startDateInvalid}
			endDateInvalid={endDateInvalid}
			handleClick={handleClick}
			handleClose={handleClose}
			handleGoToNextMonth={handleGoToNextMonth}
			handleGoToPreviousMonths={handleGoToPreviousMonths}
			handleClickOption={handleClickOption}
			setStartDate={setStartDate}
			setEndDate={setEndDate}
			handleSelectDate={handleSelectDate}
			handleChangeStartDate={handleChangeStartDate}
			handleChangeEndDate={handleChangeEndDate}
			handleLeaveFocus={handleLeaveFocus}
			clearFilter={clearFilter}
		/>
	) : (
		<CustomDatePicker
			{...props}
			state={state}
			startDateInvalid={startDateInvalid}
			endDateInvalid={endDateInvalid}
			handleClick={handleClick}
			handleClose={handleClose}
			handleGoToNextMonth={handleGoToNextMonth}
			handleGoToPreviousMonths={handleGoToPreviousMonths}
			handleClickOption={handleClickOption}
			setStartDate={setStartDate}
			setEndDate={setEndDate}
			handleSelectDate={handleSelectDate}
			handleChangeStartDate={handleChangeStartDate}
			handleChangeEndDate={handleChangeEndDate}
			handleLeaveFocus={handleLeaveFocus}
		/>
	);
}

export default memo(DatePicker);
